import React from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { MapPin, Globe, Users, Award, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_casaloco-flavors/artifacts/mrxtkbpi_Casa%20Loco%20Logo%20%285%29.png";

const AboutPage = () => {
  return (
    <Layout>
      <SEO
        title="About Casa Loco | Authentic Mexican Restaurant in Hyderabad"
        description="From Vietnam to India — discover the story behind Casa Loco, Hyderabad's best authentic Mexican restaurant. Meet our founders and explore our culinary journey."
        path="/about"
      />
      <div className="min-h-screen paper-texture" style={{ paddingTop: '76px' }} data-testid="about-page">
        {/* Hero */}
        <div className="py-16" style={{ backgroundColor: '#51A262', borderBottom: '4px solid #1A1918' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <img src={LOGO_URL} alt="Casa Loco" className="h-28 w-auto mx-auto mb-6" data-testid="about-logo" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4" style={{ textShadow: '3px 3px 0 rgba(0,0,0,0.3)' }}>
              OUR STORY
            </h1>
            <div className="zigzag-divider-gold max-w-xs mx-auto mb-4" />
            <p className="text-white/90 font-mono text-sm max-w-lg mx-auto italic">
              Authentic Mexican, Sized and Spiced for India
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Origin Story */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-6" style={{ color: '#1A1918' }}>
                FROM VIETNAM TO INDIA
              </h2>
              <div className="space-y-4 font-mono text-[#4A4641] text-sm leading-relaxed">
                <p>
                  <span className="font-bold" style={{ color: '#EE2535' }}>We are Casa Loco</span> -- and our journey to bring authentic Mexican food to India is a story of passion, flavor, and fiesta! We started in the beautiful coastal regions of Vietnam, where we developed our concept to serve genuine Mexican cuisine.
                </p>
                <p>
                  Today, we proudly operate three successful outlets across Da Nang, Hoi An, and An Bang in Vietnam, and now we've brought our vibrant fiesta to India! Our expansion to Hyderabad marks a strategic milestone.
                </p>
                <p>
                  We chose Hyderabad for a reason -- the city has emerged as a hub for international cuisine launches. With its young, dynamic population and openness to global food experiences, Hyderabad was the perfect place for our Indian debut.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square overflow-hidden" style={{ border: '4px solid #1A1918', boxShadow: '8px 8px 0 #EE2535' }}>
                <img src="https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/gtr04py8_WhatsApp%20Image%202026-03-26%20at%2012.30.52%20%281%29.jpeg" alt="Casa Loco Hyderabad Entrance" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-4 -right-4 p-5 text-center text-white" style={{ backgroundColor: '#EE2535', border: '3px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
                <Globe className="w-6 h-6 mx-auto mb-1" />
                <span className="font-display text-lg">NOW IN</span>
                <p className="font-mono text-sm font-bold">INDIA</p>
              </div>
            </div>
          </div>

          {/* Meet the Founders */}
          <div className="mb-20">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-center mb-4" style={{ color: '#1A1918' }}>
              MEET THE FOUNDERS
            </h2>
            <p className="text-center font-mono text-[#4A4641] text-sm max-w-xl mx-auto mb-10">
              The duo behind the fiesta
            </p>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Chef John */}
              <div className="bg-white" style={{ border: '3px solid #1A1918', boxShadow: '5px 5px 0 #1A1918' }}>
                <div className="flex justify-center pt-6 px-6">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full overflow-hidden" style={{ border: '4px solid #1A1918' }}>
                    <img src="https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/ts96h3li_IMG-20260327-WA0076.jpg" alt="Co-founder and Chef John Dunlea" className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                <div className="p-6 text-center">
                  <span className="inline-block text-[10px] font-mono font-bold px-2 py-0.5 mb-2 uppercase tracking-wider" style={{ backgroundColor: '#EE2535', color: 'white' }}>The Chef</span>
                  <h3 className="font-display text-2xl mb-3" style={{ color: '#1A1918' }}>JOHN DUNLEA</h3>
                  <p className="font-mono text-[#4A4641] text-sm leading-relaxed">
                    For John, food has always been at the heart of life. Growing up in a cafe and hotel family, he discovered early on the joy of creating meals that bring people together. Over the past 30 years, John has cooked everywhere from seafood restaurants and Italian kitchens to his own bustling venues, including a pizza chain, BBQ smokehouse, and bakery on the Gold Coast. Now based in Vietnam, he's poured that lifetime of passion into Casa Loco, where bold flavors and good times are always the focus.
                  </p>
                </div>
              </div>

              {/* Wayne */}
              <div className="bg-white" style={{ border: '3px solid #1A1918', boxShadow: '5px 5px 0 #1A1918' }}>
                <div className="flex justify-center pt-6 px-6">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full overflow-hidden" style={{ border: '4px solid #1A1918' }}>
                    <img src="https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/03b8p9kt_Screenshot_20260327_132542_WhatsApp.jpg" alt="Co-founder Wayne" className="w-full h-full object-cover object-center" />
                  </div>
                </div>
                <div className="p-6 text-center">
                  <span className="inline-block text-[10px] font-mono font-bold px-2 py-0.5 mb-2 uppercase tracking-wider" style={{ backgroundColor: '#51A262', color: 'white' }}>The Vision</span>
                  <h3 className="font-display text-2xl mb-3" style={{ color: '#1A1918' }}>WAYNE</h3>
                  <p className="font-mono text-[#4A4641] text-sm leading-relaxed">
                    As John's business partner for over two decades, Wayne is the strategic vision behind the Casa Loco experience. After owning and operating five Pizza Capers stores and a large American BBQ smokehouse with a Tex-Mex twist in Australia alongside John, Wayne settled in Vietnam and launched Diner 66. Spotting a gap in the market for authentic Mexican dining, he partnered with John once more to bring Casa Loco to life, starting with the flagship location on An Bang beach and later expanding to Hoi An and Da Nang.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* India Launch */}
          <div className="mb-20 p-8 lg:p-12" style={{ backgroundColor: '#FFF', border: '4px solid #1A1918', boxShadow: '6px 6px 0 #1A1918' }}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img src="https://customer-assets.emergentagent.com/job_882d595f-ff74-4a1a-b1f3-db9ebf1484f1/artifacts/wgn5ctmv_WhatsApp%20Image%202026-03-26%20at%2012.30.53%20%282%29.jpeg" alt="Casa Loco Hyderabad Dining" className="w-full" style={{ border: '3px solid #1A1918' }} />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-6" style={{ color: '#1A1918' }}>OUR INDIA LAUNCH</h2>
                <div className="space-y-4 font-mono text-[#4A4641] text-sm leading-relaxed">
                  <p>Our entry into the Indian foodservice market was made possible through our partnership with Isthara, a company with over 60 multi-brand dining destinations across India.</p>
                  <p>We're proud that our launch reflects the increasing demand for international cuisines within organized urban dining formats in India.</p>
                  <p>Our Hyderabad outlet at Radisson Hitec City, Gachibowli is just the beginning. We're committed to bringing the best authentic Mexican experience to India.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mexican Sauces & Flavors */}
          <div className="mb-20" data-testid="mexico-flavors-map">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-3" style={{ color: '#1A1918' }}>
                A CULINARY JOURNEY THROUGH MEXICO
              </h2>
              <div className="zigzag-divider max-w-xs mx-auto mb-4" />
              <p className="font-mono text-[#4A4641] text-sm max-w-xl mx-auto">
                Every dish at Casa Loco tells the story of a region. Here's where our flavors come from.
              </p>
            </div>

            <div className="text-center mb-8 max-w-2xl mx-auto p-6 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #EE2535' }}>
              <h3 className="font-display text-xl mb-2" style={{ color: '#EE2535' }}>Chipotle Adobo</h3>
              <p className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: '#F7B638' }}>Used across all regions</p>
              <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                A cornerstone of Mexican flavor, chipotle chilies preserved in adobo sauce combine smoke, tang, and mild heat. Made with imported Mexican dried jalapenos, vinegar, tomato, and spices, it is used across all regions to deepen sauces and marinades.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #51A262' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#51A262' }}>Baja Fish Taco</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Baja California — North Coast</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  The Baja California peninsula is known for its abundance of fresh seafood. Casa Loco Baja Taco is a generous portion of fried mahi-mahi with lettuce, both chipotle and coriander-lime cremas, and topped with pineapple salsa. Crispy, fresh, and zesty.
                </p>
              </div>
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #F7B638' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#F7B638' }}>Birria</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Jalisco — West Mexico</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  Rich, slow-stewed, traditionally made with meat braised in a spiced broth, built on dried chillies, garlic, and warming spices. Known for bold, savory depth, a celebration dish perfect with lamb and vegetarian options.
                </p>
              </div>
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #EE2535' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#EE2535' }}>Carnitas</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Michoacan — Central Pacific</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  Carnitas (little meats) uses simple but flavorful ingredients — pork shoulder slow-cooked in its own fat with citrus, chilli, and spices until fork-tender with crispy edges. A beloved street food staple.
                </p>
              </div>
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #51A262' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#51A262' }}>Queso</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Chihuahua-Sonora — Northern States</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  A creamy cheese dip from the mix of cultures from northern Mexican states Chihuahua-Sonora and Texas-Arizona traditions. Made with melted cheese, chilies, and spices, it's smooth, rich, and designed for sharing.
                </p>
              </div>
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #F7B638' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#F7B638' }}>Tinga</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Puebla — Central East</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  Slow-cooked and features shredded meats or vegetables simmered in a smoky tomato and chipotle sauce. Bright, slightly spicy, full of flavor, a dish central to Mexican home cooking.
                </p>
              </div>
              <div className="p-5 bg-white" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #EE2535' }}>
                <h3 className="font-display text-lg mb-1" style={{ color: '#EE2535' }}>Barbacoa</h3>
                <p className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EE2535' }}>Central Mexico</p>
                <p className="font-mono text-[#4A4641] text-xs leading-relaxed">
                  An ancient cooking style from central Mexico, where meat is slow-cooked in an underground pit or oven, wrapped in maguey leaves. The result is incredibly tender, deeply flavored meat that falls apart at the touch.
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
            {[
              { icon: MapPin, num: '4', label: 'Locations Worldwide', bg: '#EE2535' },
              { icon: Globe, num: '2', label: 'Countries', bg: '#51A262' },
              { icon: Users, num: '1000+', label: 'Happy Guests Daily', bg: '#F7B638', dark: true },
              { icon: Award, num: '#1', label: 'Best Mexican in India', bg: '#1A1918' }
            ].map((s, i) => (
              <div key={i} className="p-5 text-center" style={{ backgroundColor: s.bg, border: '3px solid #1A1918', boxShadow: '4px 4px 0 #1A1918', color: s.dark ? '#1A1918' : 'white' }}>
                <s.icon className="w-8 h-8 mx-auto mb-2" />
                <span className="font-display text-3xl block">{s.num}</span>
                <p className={`font-mono text-xs ${s.dark ? 'text-[#4A4641]' : 'text-white/80'}`}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Values */}
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl" style={{ color: '#1A1918' }}>WHAT MAKES US SPECIAL</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { title: 'Authentic Flavors', desc: 'We bring genuine Mexican recipes from our Vietnam kitchens to India. Every dish is crafted with imported ingredients and traditional techniques.', bg: '#EE2535' },
              { title: 'Fiesta Vibes', desc: 'Our Lucha Libre-themed decor, vibrant colors, and lively atmosphere transport you straight to Mexico. Every visit is a celebration!', bg: '#51A262' },
              { title: 'Sized for India', desc: "While staying true to authentic Mexican flavors, we've carefully adapted our spice levels and portion sizes to suit Indian palates.", bg: '#F7B638' }
            ].map((v, i) => (
              <div key={i} className="bg-white p-6 text-center transition-transform hover:translate-x-[-3px] hover:translate-y-[-3px]" style={{ border: '3px solid #1A1918', boxShadow: '4px 4px 0 #1A1918' }}>
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: v.bg, border: '2px solid #1A1918' }}>
                  <Star size={24} fill="white" stroke="none" />
                </div>
                <h3 className="font-display text-xl mb-3" style={{ color: '#1A1918' }}>{v.title}</h3>
                <p className="font-mono text-[#4A4641] text-xs">{v.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="p-8 lg:p-12 text-center text-white" style={{ backgroundColor: '#EE2535', border: '4px solid #1A1918', boxShadow: '6px 6px 0 #1A1918' }}>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-4" style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
              COME EXPERIENCE THE FIESTA!
            </h2>
            <p className="font-mono text-white/90 max-w-xl mx-auto mb-8 text-sm">
              Visit us at Radisson Hitec City, Gachibowli, Hyderabad!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/food-menu" className="inline-block px-8 py-3 font-display tracking-wider text-base transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]" data-testid="about-menu-btn"
                style={{ backgroundColor: '#F7B638', color: '#1A1918', border: '3px solid #1A1918', boxShadow: '3px 3px 0 #1A1918' }}>
                VIEW MENU
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AboutPage;